import React from "react";
import PropTypes from "prop-types";
import { ContentDropdown } from "@learningblocks/shared-components";
import { QuestionWrapper } from "../Components/QuestionWrapper";
import { Question } from "../Components/Question";
import { MainTitle } from "../Components/MainTitle";
import { QuestionImage } from "../Components/QuestionImage";
import { Utilities } from "@learningblocks/exp-constants";
import { Option } from "./Option";
import { SliderGroup } from "../Components/SliderGroup";
import { OverlaySlider } from "../Components/OverlaySlider";
import { Footer } from "../Components/Footer";
import { SubmitButton } from "../Components/SubmitButton";
import { TotalAttempts } from "../Components/TotalAttempts";
import "./style.scss";

import { DataTable } from "@learningblocks/shared-components";

const { QUESTION_CONTENTS } = Utilities;

/**
 * SingleSelect [CSCQ]:
 *  - You will have n choices to select from
 *  - The question will have a single correct Answer.
 */
export function SingleSelect(props) {
  const checkCSCQHasUnsubmittedOptions = (optionList) => {
    const hasUnsubmittedOption = optionList.reduce((boolVal, option) => {
      if (option.disabled === false && option.selected === true) {
        boolVal = true;
      }
      return boolVal;
    }, false);
    return hasUnsubmittedOption === false;
  };

  const setSubmitDisabled = () => {
    if (props.complete) return true;
    else {
      let optionList = [];
      if (props.options) optionList = props.options;
      if (props.typedOptions) optionList = props.typedOptions;
      return checkCSCQHasUnsubmittedOptions(optionList);
    }
  };

  const selectOption = (params) =>
    props.selectOption({ ...params, componentID: props.id });

  const onSubmit = () => props.submitAnswer(props.id, props.type);

  const renderQuestion = (key, question) => (
    <Question
      tabIndex={"0"}
      key={key}
      text={question}
      className="cscq__question"
    />
  );

  const renderText = (key, text) => (
    <Question tabIndex={"0"} key={key} text={text} className="cscq__content" />
  );

  const renderImage = (key, image) => (
    <QuestionImage
      tabIndex={"0"}
      key={key}
      altText={props.altText}
      image={image}
      className="cscq__image"
      preview={props.preview}
    />
  );

  const renderTable = (key, table) => (
    <div style={{ marginBottom: "16px" }}>
      <DataTable indicies={table.indicies} data={table.data} />
    </div>
  );

  const getIsCorrect = ({ id }) => {
    return props.answerId.includes(id);
  };

  const renderOptions = (key, options) => {
    return options.map((option, id) => {
      const index = `${key}_${id}`;
      return (
        <Option
          key={index}
          {...option}
          index={id}
          tabIndex={"0"}
          selectOption={selectOption}
          isCorrect={getIsCorrect(option)}
        />
      );
    });
  };

  const renderOverlaySlider = (key, slider) => {
    return (
      <OverlaySlider
        sequence={slider.sequence}
        highlight={[
          slider.current.start,
          slider.current.end - slider.current.start,
        ]}
        highlightClass={slider.highlightClass}
        disable={true}
      />
    );
  };

  const renderSliderGroups = (key, sliderGroups) => {
    return sliderGroups.map((slider, id) => (
      <SliderGroup key={`${key}_${id}`} {...slider} />
    ));
  };

  const hintDetailsSection = (key, hintDetails) => {
    return <ContentDropdown key={key} contentDetails={hintDetails} />;
  };

  const renderByType = (type, key, content) => {
    switch (type) {
      case QUESTION_CONTENTS.QUESTION:
        return renderQuestion(key, content);
      case QUESTION_CONTENTS.SLIDER:
        return renderOverlaySlider(key, content);
      case QUESTION_CONTENTS.TEXT:
        return renderText(key, content);
      case QUESTION_CONTENTS.IMAGE:
        return renderImage(key, content);
      case QUESTION_CONTENTS.TABLE:
        return renderTable(key, content);
      case QUESTION_CONTENTS.OPTIONS:
      case QUESTION_CONTENTS.TYPED_OPTIONS:
        return renderOptions(key, content);
      case QUESTION_CONTENTS.SLIDER_GROUPS:
        return renderSliderGroups(key, content);
      case QUESTION_CONTENTS.HINTS:
        return hintDetailsSection(key, props.hintDetails);
      case QUESTION_CONTENTS.CONTENT_LIST:
        return content.map((listItem, subKey) =>
          renderByType(listItem.type, `${key}_${subKey}`, listItem.content)
        );
      default:
        return null;
    }
  };

  const renderHint = () => {
    if (props.id == 65)
      return (
        <div>
          Hint: The independent variable is the variable that is controlled
          between the experimental groups, and the dependent variable is what is
          being measured.
        </div>
      );
    else return null;
  };

  const wrapperClass = ` cscq ${props.className}`;

  return (
    <QuestionWrapper className={wrapperClass}>
      <MainTitle title={props.title} className="cscq__mainTitle" />
      {props.content.map((type, key) => renderByType(type, key, props[type]))}
      <Footer className="cscq__footer">
        <TotalAttempts
          totalAttempts={props.totalAttempts}
          className="cscq__footer__totalAttempts"
        />
        <SubmitButton disabled={setSubmitDisabled()} onSubmit={onSubmit} />
      </Footer>
      {renderHint()}
    </QuestionWrapper>
  );
}

SingleSelect.propTypes = {
  /**
   * The prop for the ID of correct answer option [ -1 means all answers are correct]
   */
  answerId: PropTypes.arrayOf(PropTypes.number).isRequired,
  /**
   * The custom classname you wanna inject on the root of the component
   */
  className: PropTypes.string,
  /**
   * The prop to set if the component is complete
   */
  complete: PropTypes.bool.isRequired,
  /**
   * List of static contents of type question, text, and image you want to display
   */
  contentList: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Type of content Item
       */
      type: PropTypes.oneOf(["question", "image", "text"]),
      /**
       * Content Item assicated with the type
       */
      content: PropTypes.string,
    })
  ),
  /**
   * The prop to specify all contents in the question
   */
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * The prop that hold the unique id for the component
   */
  id: PropTypes.number,
  /**
   * The prop that hold image data
   */
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * The prop that hold the array of answer options.
   */
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
  
    * The prop that holds the text for the question
     */
  question: PropTypes.string.isRequired,
  /**
   * The prop that holds the total attempts,
   */
  totalAttempts: PropTypes.number.isRequired,
  /**
   * The prop that holds the Component type.
   */
  type: PropTypes.oneOf(["CSCQ_COLOR", "CSCQ", "CSCQ_NEW"]),
  /**
   * The callback prop executed when submit is clicked
   */
  submitAnswer: PropTypes.func.isRequired,
  /**
   * The callback to be executed when an option is selected
   */
  selectOption: PropTypes.func.isRequired,
  /**
   * The content that is shown in the hint sectoin
   */
  hintDetails: PropTypes.shape({
    /**
     * The label that is added to the hint section. Usually Hint
     */
    label: PropTypes.string.isRequired,
    /**
     * Description provided for the hint section
     */
    description: PropTypes.string.isRequired,
    /**
     * To show the question title
     */
    title: PropTypes.string,
    /**
     * className to be set for CSCQColor
     */
    className: PropTypes.string,
  }),
  /**
   * list of sliders you wanna display on group
   */
  sliderGroups: PropTypes.shape({
    /**
     * The prop that hold the unique id for the component
     */
    id: PropTypes.number.isRequired,
    /**
     * Label for the slider
     */
    label: PropTypes.number,
    /**
     * list of sliders you wanna display on group
     */
    sliders: PropTypes.array,
    preview: PropTypes.bool,
  }),
};

SingleSelect.defaultProps = {
  className: "",
  type: "CSCQ",
  answerId: 0,
  complete: false,
  content: [],
  image: "",
  options: [],
  question: "",
  totalAttempts: 0,
  caption: "",
  title: "single select question",
  preview: false,
};
